import { Add as AddIcon, ExpandMore as ExpandMoreIcon } from '@/components/ui/qicons';
import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  IconButton,
  Chip,
} from '@mui/material';
import { navigationConfig, NavigationItem, NavigationSection } from './navigationConfig';

interface FlattenedNavItem {
  label: string;
  path: string;
  parentLabel?: string;
}

export const SecondaryNavBar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [subMenuAnchor, setSubMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeSubMenu, setActiveSubMenu] = useState<NavigationItem | null>(null);

  // Find current section based on path
  const currentSection = useMemo((): NavigationSection | undefined => {
    for (const section of navigationConfig) {
      const flatChildren = flattenNavItems(section.children);
      if (flatChildren.some(child =>
        location.pathname === child.path ||
        location.pathname.startsWith(child.path + '/')
      )) {
        return section;
      }
    }
    return undefined;
  }, [location.pathname]);

  // Flatten navigation items for easier matching
  function flattenNavItems(items: NavigationItem[], parentLabel?: string): FlattenedNavItem[] {
    const result: FlattenedNavItem[] = [];
    for (const item of items) {
      result.push({ label: item.label, path: item.path, parentLabel });
      if (item.children) {
        result.push(...flattenNavItems(item.children, item.label));
      }
    }
    return result;
  }

  // Get visible tabs for current section
  const visibleTabs = useMemo(() => {
    if (!currentSection) return [];
    return currentSection.children;
  }, [currentSection]);

  // Find active tab index - prefer longest matching path to handle nested routes correctly
  const activeTabIndex = useMemo(() => {
    if (!currentSection) return -1;

    let bestMatchIndex = -1;
    let bestMatchLength = 0;

    for (let i = 0; i < visibleTabs.length; i++) {
      const tab = visibleTabs[i];

      // Check exact match first (highest priority)
      if (location.pathname === tab.path) {
        return i;
      }

      // Check if current path starts with tab path (prefix match)
      if (location.pathname.startsWith(tab.path + '/') && tab.path.length > bestMatchLength) {
        bestMatchIndex = i;
        bestMatchLength = tab.path.length;
      }

      // Check children for matches
      if (tab.children) {
        for (const child of tab.children) {
          if (location.pathname === child.path) {
            return i;
          }
          if (location.pathname.startsWith(child.path + '/') && child.path.length > bestMatchLength) {
            bestMatchIndex = i;
            bestMatchLength = child.path.length;
          }
        }
      }
    }

    return bestMatchIndex >= 0 ? bestMatchIndex : 0;
  }, [location.pathname, visibleTabs, currentSection]);

  const handleTabClick = (event: React.SyntheticEvent, index: number) => {
    const tab = visibleTabs[index];
    if (tab) {
      if (tab.children && tab.children.length > 0) {
        // Show submenu for items with children
        setActiveSubMenu(tab);
        setSubMenuAnchor(event.currentTarget as HTMLElement);
      } else {
        navigate(tab.path);
      }
    }
  };

  // Handle direct click on tab (works even when tab is already selected)
  const handleTabDirectClick = (event: React.MouseEvent, tab: NavigationItem) => {
    if (tab.children && tab.children.length > 0) {
      // Always show submenu for items with children, even if already selected
      setActiveSubMenu(tab);
      setSubMenuAnchor(event.currentTarget as HTMLElement);
      event.stopPropagation();
    }
  };

  const handleSubMenuClose = () => {
    setSubMenuAnchor(null);
    setActiveSubMenu(null);
  };

  const handleSubMenuItemClick = (path: string) => {
    navigate(path);
    handleSubMenuClose();
  };

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleMoreClose = () => {
    setMoreMenuAnchor(null);
  };

  // Don't render if no current section or on home/dashboard
  if (!currentSection || currentSection.id === 'dashboard') {
    return null;
  }

  // Current section label for context
  const sectionLabel = currentSection.label;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: 40,
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: 2,
      }}
    >
      {/* Section Label */}
      <Chip
        label={sectionLabel}
        size="small"
        sx={{
          mr: 2,
          backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(59, 130, 246, 0.2)'
            : 'rgba(59, 130, 246, 0.1)',
          color: theme.palette.secondary.main,
          fontWeight: 500,
          '& .MuiChip-label': {
            px: 1,
          },
        }}
      />

      {/* Secondary Tabs */}
      {!isMobile ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          <Tabs
            value={activeTabIndex >= 0 ? activeTabIndex : false}
            onChange={handleTabClick}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.secondary.main,
                height: 2,
              },
              '& .MuiTab-root': {
                minHeight: 40,
                py: 0,
                px: 2,
              },
            }}
          >
            {visibleTabs.map((tab) => (
              <Tab
                key={tab.path}
                onClick={(event) => handleTabDirectClick(event, tab)}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" noWrap>
                      {tab.label}
                    </Typography>
                    {tab.children && tab.children.length > 0 && (
                      <ExpandMoreIcon sx={{ fontSize: 16 }} />
                    )}
                  </Box>
                }
                sx={{
                  textTransform: 'none',
                  color: theme.palette.text.secondary,
                  '&.Mui-selected': {
                    color: theme.palette.text.primary,
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.04)',
                  },
                }}
              />
            ))}
          </Tabs>

          {/* Add button for future expansion */}
          <IconButton
            size="small"
            sx={{
              ml: 1,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            onClick={handleMoreClick}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        // Mobile: Show current page name with dropdown
        <Box sx={{ flex: 1 }}>
          <IconButton onClick={handleMoreClick} sx={{ color: theme.palette.text.primary }}>
            <Typography variant="body2" sx={{ mr: 0.5 }}>
              {visibleTabs[activeTabIndex]?.label || 'Select'}
            </Typography>
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Submenu for items with children */}
      <Menu
        anchorEl={subMenuAnchor}
        open={Boolean(subMenuAnchor)}
        onClose={handleSubMenuClose}
        PaperProps={{
          sx: {
            maxHeight: 300,
            minWidth: 200,
          },
        }}
      >
        {activeSubMenu?.children?.map(child => (
          <MenuItem
            key={child.path}
            onClick={() => handleSubMenuItemClick(child.path)}
            selected={location.pathname === child.path}
          >
            <Typography variant="body2">{child.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* More menu for all tabs (mobile or overflow) */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={handleMoreClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            minWidth: 220,
          },
        }}
      >
        {visibleTabs.map(tab => (
          <React.Fragment key={tab.path}>
            <MenuItem
              onClick={() => {
                if (tab.children && tab.children.length > 0) {
                  // Navigate to first child
                  navigate(tab.children[0].path);
                } else {
                  navigate(tab.path);
                }
                handleMoreClose();
              }}
              selected={location.pathname.startsWith(tab.path)}
              sx={{ fontWeight: 500 }}
            >
              {tab.label}
            </MenuItem>
            {tab.children?.map(child => (
              <MenuItem
                key={child.path}
                onClick={() => {
                  navigate(child.path);
                  handleMoreClose();
                }}
                selected={location.pathname === child.path}
                sx={{ pl: 4 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {child.label}
                </Typography>
              </MenuItem>
            ))}
          </React.Fragment>
        ))}
      </Menu>
    </Box>
  );
};
